import { EditOutlined } from "@ant-design/icons";
import "./inline-edit-title.scss";
import { Button, Form, Input } from "antd";
import { validatorForFieldValue } from "utils/utils";
import { ErrorMessages } from "constants/index";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { ProjectContext } from "context/ProjectProvider";

function InlineEditTitle(props: {
  isPermissionToEdit: boolean;
  headerInfo: any;
  updateHeaderInfo: any;
  feature: string;
}) {
  const { isPermissionToEdit, headerInfo, updateHeaderInfo, feature } = props;
  const { tokenRetrievalState } = useContext(ProjectContext);
  const [numberEditing, setNumberEditing] = useState(false);
  const [titleEditing, setTitleEditing] = useState(false);
  const featureNumberInputRef: any = useRef();
  const featureTitleInputRef: any = useRef();

  const [form] = Form.useForm();
  const titleTxt = Form.useWatch("title", form);
  const numberTxt = Form.useWatch("number", form);

  const featureNumberEmptyMsg = useMemo(() => {
    switch (feature) {
      case "BID_PACKAGE":
        return ErrorMessages.bidPackgeNumber;
      case "DESIGN_PACKAGE":
        return ErrorMessages.designPackgeNumber;
      default:
        return "";
    }
  }, [feature]);

  const featureTitleEmptyMsg = useMemo(() => {
    switch (feature) {
      case "BID_PACKAGE":
        return ErrorMessages.bidPackgeTitle;
      case "DESIGN_PACKAGE":
        return ErrorMessages.designPackgeTitle;
      case "SUBMITTALS":
        return ErrorMessages.submittalTitle;
      case "MATERIALS":
        return ErrorMessages.materialTitle;
      default:
        return "";
    }
  }, [feature]);

  const featureNumberErrorMsg = useMemo(() => {
    switch (feature) {
      case "BID_PACKAGE":
        return ErrorMessages.BidPackageNumberMsg;
      case "DESIGN_PACKAGE":
        return ErrorMessages.DesignPackageNumberMsg;
      default:
        return "";
    }
  }, [feature]);

  const featureTitleErrorMsg = useMemo(() => {
    switch (feature) {
      case "BID_PACKAGE":
        return ErrorMessages.BidPackageTitleMsg;
      case "DESIGN_PACKAGE":
        return ErrorMessages.DesignPackageTitleMsg;
      case "SUBMITTALS":
        return ErrorMessages.SubmittalTitleMsg;
      case "MATERIALS":
        return ErrorMessages.MaterialTitleMsg;
      default:
        return "";
    }
  }, [feature]);

  const isFeatureNumberEditable = useMemo(() => {
    const features = ["SUBMITTALS", "MATERIALS"];
    return !features.includes(feature);
  }, [feature]);

  useEffect(() => {
    form.setFieldsValue({
      number: headerInfo?.featureHeaderNumber ?? "",
      title: headerInfo?.featureHeaderTitle ?? ""
    });
  }, [headerInfo, form]);

  const onFinish = async (field: string) => {
    try {
      const result = await form.validateFields([field]);
      // validateFields resolves with a truthy values object on success and rejects
      // (caught below) on failure, so the false branch of this guard is unreachable.
      /* v8 ignore next */
      if (result) {
        updateHeaderInfo({
          title: `${titleTxt}`.trim(),
          number: `${numberTxt}`.trim()
        });
      }
    } catch (error: any) {
      error?.errorFields?.forEach((item: any) => {
        if (item.name.includes("title")) {
          setTitleEditing(true);
        }
        if (item.name.includes("number")) {
          setNumberEditing(true);
        }
      });
    }
  };

  return (
    <div className="items-center flex" data-testid="inline-edit-title-root">
      <Form
        className="flex gap-x-2 h-8"
        form={form}
        scrollToFirstError
        layout="vertical"
        data-testid="inline-edit-title-form"
      >
        <div className="flex" data-testid="inline-edit-title-number-row">
          <span
            className={`mt-1 font-semibold text-base !text-black max-w-xs truncate ${
              numberEditing ? "hidden" : ""
            }`}
            title={headerInfo?.featureHeaderNumber}
            data-testid="inline-edit-title-number-span"
          >
            {headerInfo?.featureHeaderNumber}
          </span>
          <Form.Item
            name="number"
            className={`${numberEditing ? "" : "hidden"}`}
            rules={[
              {
                validator: (_, value) => {
                  if (value === "") {
                    return Promise.reject(new Error(featureNumberEmptyMsg));
                  }
                  if (value === headerInfo?.featureHeaderNumber) {
                    return Promise.resolve();
                  }
                  return validatorForFieldValue(
                    "number",
                    value?.trim(),
                    feature,
                    tokenRetrievalState?.token,
                    featureNumberErrorMsg
                  );
                }
              }
            ]}
            data-testid="inline-edit-title-number-form-item"
          >
            <Input
              ref={featureNumberInputRef}
              onBlur={async (event: any) => {
                setNumberEditing(false);
                if (
                  event?.target?.value?.trim() !==
                  headerInfo?.featureHeaderNumber
                ) {
                  onFinish("number");
                }
              }}
              onPressEnter={(event: any) => {
                event?.target?.blur();
              }}
              data-testid="inline-edit-title-number-input"
            />
          </Form.Item>
          {isPermissionToEdit &&
            isFeatureNumberEditable &&
            headerInfo?.featureHeaderNumber && (
              <Button
                className="!border-none !bg-transparent hover:!bg-transparent"
                icon={<EditOutlined className="ml-2 text-color-two" />}
                onClick={() => {
                  form.setFieldValue("number", headerInfo?.featureHeaderNumber);
                  setNumberEditing(true);
                  setTimeout(() => {
                    featureNumberInputRef?.current?.focus();
                  }, 500);
                }}
                data-testid="inline-edit-title-number-edit-btn"
              />
            )}
        </div>
        <div className="flex" data-testid="inline-edit-title-title-row">
          <span
            className={`mt-1 ml-7 font-semibold text-base !text-black max-w-xs truncate ${
              titleEditing ? "hidden" : ""
            }`}
            title={headerInfo?.featureHeaderTitle}
            data-testid="inline-edit-title-title-span"
          >
            {headerInfo?.featureHeaderTitle}
          </span>
          <Form.Item
            name="title"
            className={`${titleEditing ? "" : "hidden"}`}
            rules={[
              {
                validator: (_, value) => {
                  if (value === "") {
                    return Promise.reject(new Error(featureTitleEmptyMsg));
                  }
                  if (value === headerInfo?.featureHeaderTitle) {
                    return Promise.resolve();
                  }
                  return validatorForFieldValue(
                    "title",
                    value?.trim(),
                    feature,
                    tokenRetrievalState?.token,
                    featureTitleErrorMsg
                  );
                }
              }
            ]}
            data-testid="inline-edit-title-title-form-item"
          >
            <Input
              ref={featureTitleInputRef}
              onBlur={(event: any) => {
                setTitleEditing(false);
                if (
                  event?.target?.value?.trim() !==
                  headerInfo?.featureHeaderTitle
                ) {
                  onFinish("title");
                }
              }}
              onPressEnter={(event: any) => {
                event?.target?.blur();
              }}
              data-testid="inline-edit-title-title-input"
            />
          </Form.Item>
          {isPermissionToEdit && headerInfo?.featureHeaderTitle && (
            <Button
              // `!` on border-none for the same reason as the number pencil
              // above: antd v6's hover rule out-specifies a plain utility.
              className="!border-none !bg-transparent hover:!bg-transparent"
              icon={<EditOutlined className="ml-2 text-color-two" />}
              onClick={() => {
                setTitleEditing(true);
                setTimeout(() => {
                  featureTitleInputRef?.current?.focus();
                }, 500);
              }}
              data-testid="inline-edit-title-title-edit-btn"
            />
          )}
        </div>
      </Form>
    </div>
  );
}

export default InlineEditTitle;
